import React, {useState, useEffect} from 'react'
import { StyleSheet, View, TouchableOpacity, FlatList } from 'react-native'
import { AppModal as Modal } from '../atoms/AppModal'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Text, Icon } from '../atoms'
import { MomentsList } from './MomentsList'
import { BottomModalBase } from '../molecules'
import DatePicker from 'react-native-date-picker'
import { useForm } from 'react-hook-form'
import { Disponibility, getDayLabel } from '@utils'
import { Moment } from '@hooks'
import { useLanguage } from '@context'

export const days = [
  { id: '0', name: 'Dimanche' },
  { id: '1', name: 'Lundi' },
  { id: '2', name: 'Mardi' },
  { id: '3', name: 'Mercredi' },
  { id: '4', name: 'Jeudi' },
  { id: '5', name: 'Vendredi' },
  { id: '6', name: 'Samedi' },
]

export const AvailabilityModal = ({
  isVisible,
  setIsVisible,
  disponibilities,
  setDisponibilities,
  bestMoments,
  setBestMoments,
}: {
  isVisible: boolean;
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  disponibilities: Disponibility[];
  setDisponibilities: (d: Disponibility[]) => void;
  bestMoments: Moment[];
  setBestMoments: (p: Moment[]) => void;
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyle(theme)
  const { control } = useForm({
    defaultValues: {
      preferences: []
    }
  })
  // Day picker modal state
  const [dayPicker, setDayPicker] = useState<{ idx: number; type: 'start' | 'end' } | null>(null);
  // Time picker modal state
  const [timePicker, setTimePicker] = useState<null | { idx: number; type: 'start' | 'end' }>(null);
  const [pickerTime, setPickerTime] = useState(new Date());

  const updateRange = (idx: number, newRange: typeof disponibilities[0]) => {
    setDisponibilities(disponibilities.map((r, i) => i === idx ? newRange : r));
  };
  const removeRange = (idx: number) => {
    setDisponibilities(disponibilities.filter((_, i) => i !== idx));
  };
  const addRange = () => {
    setDisponibilities([
      ...disponibilities,
      { dayStart: days[0], dayEnd: days[6], timeStart: '08:00', timeEnd: '18:00' }
    ])
  }


  useEffect(() => {
    if (disponibilities?.length === 0) {
      addRange()
    }
  }, [disponibilities?.length])

  const handleToggleMoment = (moment: Moment) => {
    const newBestMoments = bestMoments.find(m => m.id === moment.id)
      ? bestMoments.filter(m => m.id !== moment.id)
      : [...bestMoments, moment]
    setBestMoments(newBestMoments)
  }

  return (
    <BottomModalBase isVisible={isVisible} setIsVisible={setIsVisible}>
      <View style={styles.container}>  
        <View style={styles.titleContainer}>
          <Text type="titleBold">{t('form.availability')}</Text>
          <TouchableOpacity onPress={addRange} style={styles.addRangeButton}>
            <Icon name="plus" size={theme.iconSize.large} color={theme.colors.black} />
          </TouchableOpacity>
        </View>
        {disponibilities?.map((range, idx) => (
          <View key={idx} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
            {/* Day range clickable */}
            <TouchableOpacity onPress={() => setDayPicker({ idx, type: 'start' })} style={styles.dayBox}>
              <Text type="body">{(getDayLabel(t, range.dayStart?.id) || range.dayStart?.name || '').slice(0, 3)}</Text>
            </TouchableOpacity>
            <Text type="body"> - </Text>
            <TouchableOpacity onPress={() => setDayPicker({ idx, type: 'end' })} style={styles.dayBox}>
              <Text type="body">{(getDayLabel(t, range.dayEnd?.id) || range.dayEnd?.name || '').slice(0, 3)}</Text>
            </TouchableOpacity>
            {/* Time range clickable */}
            <TouchableOpacity
              onPress={() => {
                setTimePicker({ idx, type: 'start' });
                const [h, m] = range.timeStart.split(':');
                const d = new Date();
                d.setHours(Number(h), Number(m), 0, 0);
                setPickerTime(d);
              }}
              style={{ marginLeft: 12 }}
            >
              <Text type="bodyBold">{range.timeStart}</Text>
            </TouchableOpacity>
            <Text type="body"> - </Text>
            <TouchableOpacity
              onPress={() => {
                setTimePicker({ idx, type: 'end' });
                const [h, m] = range.timeEnd.split(':');
                const d = new Date();
                d.setHours(Number(h), Number(m), 0, 0);
                setPickerTime(d);
              }}
            >
              <Text type="bodyBold">{range.timeEnd}</Text>
            </TouchableOpacity>
            {idx > 0 && <TouchableOpacity onPress={() => removeRange(idx)} style={styles.removeButton}>
              <Icon name="trash" size={20} color="red" />
            </TouchableOpacity>}
          </View>
        ))}
        <Text type="titleBold" style={styles.title}>{t('form.bestMoments')}</Text>
        <MomentsList 
          control={control}
          name={'' as any}
          moments={bestMoments}
          onToggle={handleToggleMoment}
          height={300}
        />
      </View>
      {/* Day picker modal */}
      <Modal
        visible={!!dayPicker}
        transparent
        animationType="fade"
        onRequestClose={() => setDayPicker(null)}
      >
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.3)', justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: 'white', borderRadius: 16, padding: 16, width: 250 }}>
            <Text type="bodyBold" style={{ marginBottom: 12 }}>{t('form.chooseDay')}</Text>
            <FlatList
              data={days}
              keyExtractor={item => item.id}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={{ padding: 12, alignItems: 'center' }}
                  onPress={() => {
                    if (dayPicker) {
                      const { idx, type } = dayPicker;
                      updateRange(idx, {
                        ...disponibilities[idx],
                        [type === 'start' ? 'dayStart' : 'dayEnd']: item
                      });
                      setDayPicker(null);
                    }
                  }}
                >
                  <Text type="body">{getDayLabel(t, item.id)}</Text>
                </TouchableOpacity>
              )}
            />
            <TouchableOpacity onPress={() => setDayPicker(null)} style={{ marginTop: 8, alignSelf: 'center' }}>
              <Text type="bodyBold">{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
      {/* Time picker modal */}
      <Modal
        visible={!!timePicker}
        transparent
        animationType="fade"
        onRequestClose={() => setTimePicker(null)}
      >
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.3)', justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: 'white', borderRadius: 16, padding: 16, width: 300, alignItems: 'center' }}>
            <Text type="bodyBold" style={{ marginBottom: 12 }}>{t('form.chooseTime')}</Text>
            <DatePicker
              date={pickerTime}
              onDateChange={setPickerTime}
              mode="time"
              locale="fr"
              is24hourSource="locale"
            />
            <View style={{ flexDirection: 'row', marginTop: 16 }}>
              <TouchableOpacity
                onPress={() => setTimePicker(null)}
                style={{ marginRight: 24 }}
              >
                <Text type="bodyBold">{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => {
                  if (timePicker) {
                    const { idx, type } = timePicker;
                    const h = pickerTime.getHours().toString().padStart(2, '0');
                    const m = pickerTime.getMinutes().toString().padStart(2, '0');
                    updateRange(idx, {
                      ...disponibilities[idx],
                      [type === 'start' ? 'timeStart' : 'timeEnd']: `${h}:${m}`
                    });
                    setTimePicker(null);
                  }
                }}
              >
                <Text type="bodyBold">{t('common.validate')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </BottomModalBase>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      width: '100%',
      paddingBottom: theme.spacing.xl,
      paddingHorizontal: theme.spacing.xl,
    },
    titleContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingBottom: theme.spacing.lg,
      gap: theme.spacing.lg,
    },
    addRangeButton: {
      borderRadius: 20,
      backgroundColor: theme.colors.primary + 20,
      padding: theme.spacing.xs,
    },
    dayBox: {
      borderWidth: 1,
      borderColor: '#ccc',
      borderRadius: 6,
      paddingHorizontal: 10,
      paddingVertical: 4,
      marginHorizontal: 2,
      minWidth: 48,
      alignItems: 'center',
    },
    removeButton: {
      flex: 1,
      alignItems: 'flex-end',
    },
    title: {
      paddingTop: theme.spacing.xl,
      paddingBottom: theme.spacing.md,
    },
  }) 